import Record from "slotworks/model/gameplay/records/record";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import { DataProcessorSupplement } from "slotworks/model/gameplay/supplements/data-processor-supplement";
import SlotDefinition from "slotworks/model/slot-definition";
import Point from "slotworks/utils/geom/point";
import { AnticipationResult } from "../records/results/anticipation-result";

class AnticipationSupplement implements DataProcessorSupplement {

    public process(record: Record) {

        if (SlotDefinition.symbolDefinitions) {
            if (record instanceof SpinRecord && record.grid) {

                const anticipationResult: AnticipationResult = new AnticipationResult();

                anticipationResult.reelAnticipations = [false];
                anticipationResult.landSymbols = [];
                anticipationResult.landBonuses = [];
                anticipationResult.anticipateSymbols = [[]];

                const symbolAnticipations: Map<string, SymbolAnticipateCount> = new Map<string, SymbolAnticipateCount>();

                const reels: string[][] = record.grid;

                // Create anticipation count for all symbols
                SlotDefinition.symbolDefinitions.forEach((symbol) => {
                    symbolAnticipations.set(symbol.id, { count: 0, anticipate: false, gap: false, bonusId: symbol.bonusId });
                });

                for (let reel = 0; reel < reels.length; reel++) {

                    anticipationResult.landSymbols[reel] = [];
                    anticipationResult.landBonuses[reel] = [];
                    anticipationResult.anticipateSymbols[reel + 1] = [];

                    let row = -1;

                    for (const symbolId of reels[reel]) {
                        row++;

                        const reelSymbol = SlotDefinition.getSymbolDefinition(symbolId);
                        const symbolAliases = [reelSymbol];

                        // If a wild counts towards a bonus, swap symbol for that bonus
                        if (reelSymbol.wild) {
                            for (const newSymbol of SlotDefinition.symbolDefinitions) {
                                if (newSymbol.bonusId > 0 && newSymbol.includeWilds) {
                                    symbolAliases.push(newSymbol);
                                }
                            }
                        }

                        for (const symbol of symbolAliases) {
                            // Skip if symbol has no anticipation, don't track it
                            if (symbol.matchesMin === 0) {
                                continue;
                            }

                            // Skip if the symbol is not on a valid row
                            if (symbol.validRows && symbol.validRows.indexOf(row) === -1) {
                                continue;
                            }

                            // Update count of symbols hit
                            const symbolAnticipation = symbolAnticipations.get(symbol.id);
                            symbolAnticipation.gap = symbolAnticipation.count < reel;

                            if (symbol.ways && symbolAnticipation.gap) {
                                continue;
                            }

                            symbolAnticipation.count++;

                            // Update other symbol anticipation counts which share a bonus ID
                            if (symbol.bonusId) {
                                symbolAnticipations.forEach((otherSymbolAnticipation) => {
                                    if (otherSymbolAnticipation !== symbolAnticipation) {
                                        if (otherSymbolAnticipation.bonusId === symbolAnticipation.bonusId) {
                                            otherSymbolAnticipation.count++;
                                        }
                                    }
                                });
                            }

                            // Only play symbol lands if there is still a chance of winning a bonus
                            const stillChanceToWin = symbolAnticipation.count + (reels.length - (reel + 1)) >= symbol.matchesMin;
                            if (stillChanceToWin) {
                                anticipationResult.landSymbols[reel].push(symbolId);
                                anticipationResult.landBonuses[reel].push(symbol.bonusId);
                                anticipationResult.positions.push(new Point(reel + 1, row + 1));
                            }

                            // Check to see if anticipation is triggered yet
                            if (symbolAnticipation.count >= symbol.anticipation) {
                                // Activate the anticipation
                                symbolAnticipation.anticipate = true;
                            }
                        }
                    }

                    symbolAnticipations.forEach((symbolAnticipation, symbolId) => {
                        const symbol = SlotDefinition.getSymbolDefinition(symbolId);
                        const maxAnticipation = symbol.matchesMax === 0 ? reels.length : symbol.matchesMax;
                        const minAnticipation = symbol.matchesMin;
                        const remainingReels = reels.length - reel - 1;

                        // Deactivate anticipation when count goes over max
                        if (symbolAnticipation.count >= maxAnticipation || symbolAnticipation.count + remainingReels < minAnticipation) {
                            symbolAnticipation.anticipate = false;
                        }

                        // Deactivate anticipation when there are gaps in a ways trigger
                        if (symbolAnticipation.gap && symbol.ways) {
                            symbolAnticipation.anticipate = false;
                        } else {
                            // Turn gap on which will either be filled or not when moving to next reel
                            symbolAnticipation.gap = true;
                        }
                    });

                    anticipationResult.reelAnticipations[reel + 1] = false;

                    // If any anticipations are active, next reel should anticipate
                    symbolAnticipations.forEach((value: { count: number, anticipate: boolean }, key: string) => {
                        const symbolDef = SlotDefinition.getSymbolDefinition(key);

                        if (value.anticipate) {
                            // If the next strip doesn't contain any of the anticipation worthy symbols, it should not anticipate
                            const nextReelStripContainsSymbol = symbolDef.reels.indexOf(reel + 2) > -1;
                            if (nextReelStripContainsSymbol) {
                                anticipationResult.reelAnticipations[reel + 1] = true;
                                anticipationResult.anticipateSymbols[reel + 1].push(key);
                            }
                        }
                    });
                }

                // Second pass for symbols which always anticipate
                for (const symbolDef of SlotDefinition.symbolDefinitions) {
                    if (symbolDef.anticipation === 0 && symbolDef.matchesMin > 0) {
                        for (let reel = 0; reel < reels.length; reel++) {
                            const reelStripContainsSymbol = symbolDef.reels.indexOf(reel + 1) > -1;
                            if (reelStripContainsSymbol) {
                                anticipationResult.reelAnticipations[reel] = true;
                                anticipationResult.anticipateSymbols[reel].push(symbolDef.id);
                            }
                        }
                    }
                }

                // Last "next reel should anticipate" is irrelevant
                anticipationResult.reelAnticipations.pop();

                record.results.push(anticipationResult);
            }
        }
    }
}

export default new AnticipationSupplement();

interface SymbolAnticipateCount {
    count: number;
    anticipate: boolean;
    gap: boolean;
    bonusId: number;
}
